import { useState } from "react";
import { IFile } from "../../../common/commonTypes";
import { getHumanReadableFileSize, isVideo } from "../../../helpers/fileHelper";
import { captureException } from "@sentry/nextjs";
import { cloudinaryService } from "../../../api/cloudinaryService";
import { fileService } from "../../../api/fileService";
import { MimeEnum } from "../../../common/commonEnums";

export const useFileUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [size, setSize] = useState("0mb");
  const [uploadRequest, setUploadRequest] = useState<
    XMLHttpRequest | undefined
  >(undefined);

  const handleProgress = (e: ProgressEvent) => {
    let p = (e.loaded / e.total) * 100;
    setProgress(p);
  };

  const uploadFile = async (
    file: File,
    useCloudinaryVideo?: boolean,
    onSuccess?: (file: IFile) => void,
    thumbnailSizes?: number[]
  ) => {
    try {
      setIsLoading(true);
      setSize(getHumanReadableFileSize(file.size));

      if (isVideo(file.type as MimeEnum) && useCloudinaryVideo) {
        const uploadReq = cloudinaryService.createUploadWithProgressRequest();
        setUploadRequest(uploadReq);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "dev_test");

        const result = await fileService.uploadFileWithProgress(
          formData,
          uploadReq,
          handleProgress
        );
        const jsonResult = JSON.parse(result as any);

        const createdFile = await fileService.createFile({
          name: file.name,
          mime: file.type,
          url: jsonResult.secure_url,
        });

        if (onSuccess) {
          onSuccess(createdFile.data);
        }
      } else {
        const createdFile = await fileService.createFile({
          name: file.name,
          mime: file.type,
        });
        const uploadReq = fileService.createUploadWithProgressRequest(
          file,
          createdFile.data
        );
        setUploadRequest(uploadReq);

        await fileService.uploadFileWithProgress(
          file,
          uploadReq,
          handleProgress
        );
        await fileService.updateFile(createdFile?.data?.id, { active: true });

        if (onSuccess) {
          onSuccess(createdFile.data);
        }

        if (thumbnailSizes) {
          // not need to await here - we want to run it silently in the background
          fileService.generateThumbnails(createdFile.data.url, thumbnailSizes);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFile = async () => {
    try {
      uploadRequest?.abort();

      setUploadRequest(undefined);
      setProgress(0);
      setSize("0mb");
      setIsLoading(false);
    } catch (error) {
      captureException(error);
    }
  };

  return {
    isLoading,
    uploadFile,
    deleteFile,
    progress,
    size,
  };
};
